import {
  useGetAllUniversalGridItemsQuery as useGetUniversalItemsQuery,
  useGetAllUniversalGridItemsLazyQuery as useGetUniversalItemsLazyQuery,
  GetAllUniversalGridItemsQuery
} from '@graphql/generated';
import { useMemo } from 'react';
import { UniversalItem } from '../../../Catalog.types';
import {
  GetAllGridItemsLazyQueryOptions,
  GetAllGridItemsLazyQueryResult,
  GetAllGridItemsQueryData,
  GetAllGridItemsQueryOptions,
  GetAllGridItemsQueryResult
} from './getAllProductGridItems';

const extractUniversalItems = (fallbackData: GetAllUniversalGridItemsQuery | undefined): UniversalItem[] => {
  return fallbackData?.productUniversalSearchResults?.pagedResults?.edges.map(convertUniversalItem) ?? [];
};

const convertUniversalItem = (edge: NonNullable<GetAllUniversalGridItemsQuery['productUniversalSearchResults']['pagedResults']>['edges'][0]): UniversalItem => {
  if (edge.node.item.__typename === 'ProductUniversalCatalogItemContainer') {
    return {
      ...edge.node.item.catalogItem,
      source: edge.node.source
    };
  } else {
    return {
      ...edge.node.item.product,
      source: edge.node.source
    };
  }
};

const convertResponseData = (data?: GetAllUniversalGridItemsQuery): GetAllGridItemsQueryData | undefined => {
  if (!data) {
    return undefined;
  }
  const pagedResults = data.productUniversalSearchResults?.pagedResults;
  return {
    productCatalogCategoryTreeRoot: data.productCatalogCategoryTreeRoot,
    productUniversalSearchResults: {
      allFilters: data.productUniversalSearchResults?.allFilters,
      pagedResults: pagedResults
        ? {
            edges: pagedResults.edges.map(edge => ({
              cursor: edge.cursor,
              node: convertUniversalItem(edge)
            })),
            pageInfo: pagedResults.pageInfo
          }
        : undefined
    }
  };
};

export const useGetUniversalGridItemsQuery = (options: GetAllGridItemsQueryOptions): GetAllGridItemsQueryResult => {
  const { data, ...restQueryReturn } = useGetUniversalItemsQuery(options);

  const productList = useMemo(() => {
    return extractUniversalItems(data);
  }, [data]);

  return {
    data: convertResponseData(data),
    productList,
    ...restQueryReturn
  } as const;
};

export const useGetUniversalGridItemsLazyQuery = (options: GetAllGridItemsLazyQueryOptions): GetAllGridItemsLazyQueryResult => {
  const [sendRequest, { data, ...restQueryReturn }] = useGetUniversalItemsLazyQuery(options);

  const fallbackProductList = useMemo(() => {
    return extractUniversalItems(data);
  }, [data]);

  return {
    sendRequest,
    fallbackProductList,
    ...restQueryReturn
  } as const;
};
